import { routeUrl } from "@/app/routes";
import {
  READ_CARD_ROW_LIMIT,
  ReadCardFrame,
} from "@/features/copilot/components/read-cards/read-card-frame";
import type { LeaveBalanceItem } from "@/features/copilot/schemas/copilot-read-cards.schema";
import { Progress } from "@/shared/components/ui/progress";

/// Balances arrive as decimal strings ("4.5"); show them as written, without float noise.
function days(value: string): string {
  return `${value} ${Number(value) === 1 ? "day" : "days"}`;
}

/// `credited` already includes carried-forward days (the leave page's meter reads the same way),
/// so taken over credited is the share used.
function usedPercent(balance: LeaveBalanceItem): number | null {
  const credited = Number(balance.credited ?? "");
  const taken = Number(balance.taken ?? "");
  if (!Number.isFinite(credited) || !Number.isFinite(taken) || credited <= 0 || taken < 0) {
    return null;
  }
  return Math.min(100, (taken / credited) * 100);
}

export function LeaveBalancesReadCard({
  balances,
  slug,
}: {
  balances: LeaveBalanceItem[];
  slug: string;
}) {
  // A retired type still holding days is real leave the person can see on the leave page, so it
  // stays, tagged and after the active types.
  const ordered = [
    ...balances.filter((balance) => balance.isActive !== false),
    ...balances.filter((balance) => balance.isActive === false),
  ];
  const shown = ordered.slice(0, READ_CARD_ROW_LIMIT);
  return (
    <ReadCardFrame
      title="Your leave balances"
      viewAllHref={routeUrl.leave.mine(slug)}
      hiddenCount={ordered.length - shown.length}
    >
      {shown.map((balance) => {
        const used = usedPercent(balance);
        return (
          <li key={balance.leaveType.id} className="space-y-1.5 px-3 py-2">
            <span className="flex items-baseline justify-between gap-3">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate">{balance.leaveType.name}</span>
                {balance.isActive === false ? (
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-3xs text-text-secondary">
                    Retired
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 font-medium tabular-nums">{days(balance.available)}</span>
            </span>
            {used !== null ? (
              <>
                <Progress value={used} className="h-1.5" aria-hidden="true" />
                <span className="block text-xs text-text-secondary tabular-nums">
                  {balance.taken} taken of {balance.credited}
                </span>
              </>
            ) : null}
          </li>
        );
      })}
    </ReadCardFrame>
  );
}
